import { useCallback, useEffect, useState } from 'react';

type UseNearViewportOptions = Readonly<{
  /**
   * Fixed pixel distance before the element enters the viewport to trigger loading.
   *
   * The synchronous mount check uses on-screen detection only (no ahead-of-scroll
   * offset) — the placeholder handles the visual gap until the IO fires.
   *
   * @default 300
   */
  preloadPixels?: number;
  /**
   * Viewport-height multiplier for the IO rootMargin, used when `preloadPixels`
   * is explicitly set to `undefined`. Prefer `preloadPixels` for deterministic
   * fixed-size components; use this only when viewport-relative lookahead is
   * intentional (e.g. very tall sections).
   */
  preloadViewports?: number;
  /**
   * IntersectionObserver root element.
   * - `undefined` (default): auto-detect the nearest scrolling ancestor. This handles
   *   layouts where scrolling happens inside a div container (e.g. Brannan single-page)
   *   rather than the window. IO with `root: null` clips through ancestor overflow rects,
   *   so elements outside a scroll container's visible area would never intersect.
   * - `Element`: use the specified element as root.
   * @default undefined (auto-detect)
   */
  root?: Element;
  /**
   * When true, always returns `hasBeenNear: true`, bypassing deferral entirely.
   * Use for admin iframe preview (`isInIframe()`) and non-scroll layouts (`!isSinglePage`).
   * @default false
   */
  disabled?: boolean;
}>;

type UseNearViewportResult = Readonly<{
  /** Callback ref to attach to the sentinel element being observed. */
  ref: (el: Element | null) => void;
  /**
   * One-way latch: starts `false`, flips to `true` once the element is near the viewport.
   * Never resets back to `false` — content stays rendered once triggered.
   */
  hasBeenNear: boolean;
}>;

// 300px: enough lookahead to preload just before scroll reaches the component,
// while keeping the sync mount-check to on-screen only (no eager pre-render).
const DEFAULT_PRELOAD_PIXELS = 300;

export function useNearViewport({ preloadViewports, preloadPixels, root, disabled = false }: UseNearViewportOptions = {}): UseNearViewportResult {
  // Initialize to `disabled` on both server and client so SSR and the initial
  // client render produce identical output (no hydration mismatch).
  //
  // This is safe because NearViewport is only used to wrap page-widget content
  // (schedule, registry, travel, etc.), which is always below the fold. SSR focuses
  // on above-fold shell content (header, theme). Page widgets remain `ssr: false`
  // so their server-side output is empty loading divs — the skeleton placeholder
  // rendered here is semantically equivalent and produces no hydration mismatch.
  const [hasBeenNear, setHasBeenNear] = useState(disabled);
  const [element, setElement] = useState<Element | null>(null);

  useEffect(() => {
    if (disabled) {
      // Runtime transition: disabled changed false → true (e.g. preview state resolved
      // after mount). Flip the latch so children render immediately.
      setHasBeenNear(true);
      return;
    }
    if (hasBeenNear || !element) return;

    // Fallback for environments without IntersectionObserver (old browsers, test env).
    if (typeof IntersectionObserver === 'undefined') {
      setHasBeenNear(true);
      return;
    }

    // Auto-detect the nearest scrolling ancestor so the IO root matches the actual
    // scroll container. This is critical: IO with root=null clips through ancestor
    // overflow rects, so elements below a div scroll container (e.g. Brannan's
    // LayoutBrannanSinglePage__ScrollableContainer) report isIntersecting=false even
    // with a large rootMargin, because the container's overflow clip zeroes their
    // intersection before the rootMargin expansion is applied.
    //
    // root=undefined → auto-detect; root=Element → use it directly.
    let ioRoot: Element | null = root ?? null;
    if (root === undefined) {
      let parent = element.parentElement;
      while (parent && parent !== document.documentElement) {
        const style = getComputedStyle(parent);
        const overflow = style.overflow + style.overflowY;
        if (overflow.includes('auto') || overflow.includes('scroll')) {
          ioRoot = parent;
          break;
        }
        parent = parent.parentElement;
      }
    }

    // preloadViewports takes explicit priority when provided; otherwise fall back to
    // preloadPixels then the 300px default. This ensures callers who opt into
    // viewport-relative lookahead are not silently overridden by the pixel default.
    const isViewportMode = preloadViewports != null;
    const preloadPx = isViewportMode ? window.innerHeight * preloadViewports : (preloadPixels ?? DEFAULT_PRELOAD_PIXELS);

    // Synchronous mount check: only used in viewport mode (viewport-relative lookahead).
    // In pixel mode (the default) we skip it entirely and rely on the IO — the placeholder
    // covers the one-frame async delay invisibly, and the sync check caused false-positives
    // for sticky/near-fold elements whose getBoundingClientRect().top landed within
    // window.innerHeight at load.
    //
    // Skip it while the element has no layout box (`getClientRects()` is empty under a
    // `display: none` ancestor): its rect is all zeros, so `top` 0 would read as on screen. On a
    // hard load of the admin card editor (`/<eventHandle>/edit/cards/...`), the save-the-date
    // gallery's tiles mount while `StyledAdminRoot` is `display: none`: React 17's legacy
    // Suspense (`SuspenseWithPageLoader` in Admin.tsx) hides it that way while something else in
    // the boundary suspends, and still runs the tiles' effects. Every tile measured top 0, so 488
    // of 556 mounted at once (a ~6.6s render). The IO below reports them correctly once the root
    // is shown. Switching tabs inside the editor didn't reproduce it (nothing suspends by then).
    if (isViewportMode && element.getClientRects().length > 0) {
      if (element.getBoundingClientRect().top < window.innerHeight + preloadPx) {
        setHasBeenNear(true);
        return;
      }
    }

    // IO scroll-ahead: fires `preloadPx` before the element enters the scroll root's
    // visible area. rootMargin uses `px` because `%` with root=null has inconsistent
    // clamping behavior across browsers.
    const rootMargin = `0px 0px ${preloadPx}px 0px`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenNear(true);
          // Fire-and-forget: disconnect immediately after first trigger.
          observer.disconnect();
        }
      },
      { rootMargin, root: ioRoot },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [disabled, element, hasBeenNear, preloadPixels, preloadViewports, root]);

  // Stable callback ref: avoids re-creating the observer when the parent re-renders.
  const ref = useCallback((el: Element | null) => {
    setElement(el);
  }, []);

  return { ref, hasBeenNear };
}
